import type { Brand, Scheme } from '../providers/ThemeProvider';

// Why do we need this?
// Since we modify assets on build time, changing colors in this file will still keep consistent file names.
// For example:
// - `icon.svg` → `icon.HASH.svg`, `icon.HASH_theme=light_brand=reddish.svg`, and so on.
// Why is this a problem?
// Since we cache assets on cloudflare (prod) and cloudfront (other stages),
// we need to change the file names in order to not get the previous cached assets.
export const COLORS_VERSION = 'v1';

const PINKISH_COLORS: Readonly<Record<string, Record<Exclude<Brand, 'pinkish'>, string>>> = {
  '#ff0083': { reddish: '#f7274a', bluish: '#4269E1' },
  '#ff0082': { reddish: '#f7274a', bluish: '#4269E1' },
  '#dc0073': { reddish: '#ec092f', bluish: '#3557C9' },
  '#b40060': { reddish: '#c5001d', bluish: '#2845A1' },
  '#ef5da8': { reddish: '#ff4f6b', bluish: '#6B8AE8' },
  '#ff5cb5': { reddish: '#ff4f6b', bluish: '#6B8AE8' },
  '#ffa4d3': { reddish: '#fb9dac', bluish: '#A3B8F0' },
  '#ffb4da': { reddish: '#fb9dac', bluish: '#A3B8F0' },
  '#ffb6db': { reddish: '#fb9dac', bluish: '#A3B8F0' },
  '#ffcee7': { reddish: '#fcb5c1', bluish: '#C8D6F7' },
  '#ffd1e8': { reddish: '#fcb5c1', bluish: '#C8D6F7' },
  '#ffcce6': { reddish: '#fcb5c1', bluish: '#C8D6F7' },
  '#ffebf5': { reddish: '#fff3f5', bluish: '#EBF0FC' },
  '#fff7fb': { reddish: '#fff3f5', bluish: '#EBF0FC' },
};

const DARK_COLORS = new Set(['#4c4c4c', '#4b4b4b', '#4b4a4a', '#4a4a4a', '#444444', '#333333', '#242424', '#7b7b7b']);

const DARK_TO_LIGHT_INVERSION: Readonly<Record<string, string>> = {
  '#4c4c4c': '#e0e0e0',
  '#4b4b4b': '#e0e0e0',
  '#4b4a4a': '#e0e0e0',
  '#4a4a4a': '#e0e0e0',
  '#444444': '#e0e0e0',
  '#333333': '#dadada',
  '#242424': '#dadada',
  '#7b7b7b': '#d0d0d0',
};

const LIGHT_SCHEME_COLORS: Readonly<Record<string, string>> = {
  '#ffffff': '#e0e0e0',
  '#fff': '#e0e0e0',
  '#fefefe': '#dfdfdf',
  '#fdfdfd': '#dedede',
  '#fcfcfc': '#dddddd',
  '#fafafa': '#dcdcdc',
  '#f9f9f9': '#dbdbdb',
  '#f8f8f8': '#dadada',
  '#f8f7f7': '#d9d9d9',
  '#f7f7f7': '#d8d8d8',
  '#f6f6f6': '#d7d7d7',
  '#f5f5f5': '#d6d6d6',
  '#f4f4f4': '#d5d5d5',
  '#f3f3f3': '#d4d4d4',
  '#f2f2f2': '#d3d3d3',
  '#f1f1f1': '#d2d2d2',
  '#f0f0f0': '#d1d1d1',
  '#efefef': '#d0d0d0',
  '#ededed': '#cecece',
  '#ececec': '#cdcdcd',
  '#ebebeb': '#cccccc',
  '#eaeaea': '#cbcbcb',
  '#e9e9e9': '#cacaca',
  '#e8e8e8': '#c9c9c9',
  '#e7e7e7': '#c8c8c8',
  '#e6e6e6': '#c7c7c7',
  '#e5e5e5': '#c6c6c6',
  '#e2e2e2': '#c3c3c3',
  '#e0e0e0': '#c1c1c1',
  '#dedede': '#bfbfbf',
  '#dddddd': '#bebebe',
  '#dbdbdb': '#bcbcbc',
  '#dadada': '#bbbbbb',
  '#d9d9d9': '#bababa',
  '#d7d7d7': '#b8b8b8',
  '#d5d5d5': '#b6b6b6',
  '#d2d2d2': '#b3b3b3',
  '#cccccc': '#adadad',
  '#ccc': '#adadad',
  '#c8c8c8': '#a9a9a9',
  '#bdc3c7': '#9ea4a8',
  '#bbbbbb': '#9c9c9c',
  '#b9b9b9': '#9a9a9a',
  '#b7b7b7': '#989898',
  '#a9a9a9': '#8a8a8a',
  '#95a5a6': '#768687',
  '#7f8c8d': '#606d6e',
  '#7b7b7b': '#5c5c5c',
  '#4c4c4c': '#5c5c5c',
  '#4b4b4b': '#5b5b5b',
  '#4b4a4a': '#5b5a5a',
  '#4a4a4a': '#5a5a5a',
  '#444444': '#545454',
  '#333333': '#434343',
  '#242424': '#343434',
};

const replaceBrandColors = (svgContent: string, targetBrand: Brand): string => {
  if (targetBrand === 'pinkish') {
    return svgContent;
  }

  let modifiedSvg = svgContent;

  for (const [pinkishColor, alternatives] of Object.entries(PINKISH_COLORS)) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    const targetColor = alternatives[targetBrand];

    modifiedSvg = modifiedSvg.replaceAll(new RegExp(pinkishColor, 'gi'), targetColor);
  }

  return modifiedSvg;
};

const hasSingleColor = (svgContent: string): string | null => {
  const colorRegex = /#[0-9A-Fa-f]{6}|#[0-9A-Fa-f]{3}/g;
  const colors = new Set<string>();
  const matches = svgContent.match(colorRegex);

  if (matches == null) {
    return null;
  }

  for (const color of matches) {
    const normalizedColor = color.toLowerCase();
    colors.add(normalizedColor);
  }

  if (colors.size === 1) {
    return Array.from(colors)[0];
  }

  return null;
};

const replaceSchemeColors = (svgContent: string, originalSvg: string, targetScheme: Scheme): string => {
  if (targetScheme === 'light') {
    return svgContent;
  }

  const singleColor = hasSingleColor(originalSvg);

  let modifiedSvg = svgContent;

  if (singleColor != null && DARK_COLORS.has(singleColor)) {
    const lightColor = DARK_TO_LIGHT_INVERSION[singleColor];

    modifiedSvg = modifiedSvg.replaceAll(new RegExp(singleColor, 'gi'), lightColor);

    return modifiedSvg;
  }

  for (const [lightColor, darkColor] of Object.entries(LIGHT_SCHEME_COLORS)) {
    modifiedSvg = modifiedSvg.replaceAll(new RegExp(lightColor, 'gi'), darkColor);
  }

  return modifiedSvg;
};

export type TransformOptions = {
  readonly skipSchemeTransform?: boolean;
  readonly skipBrandTransform?: boolean;
};

export const transformSvg = (
  originalSvg: string,
  targetScheme: Scheme,
  targetBrand: Brand,
  options?: TransformOptions,
): string => {
  let result = originalSvg;

  if (options?.skipBrandTransform !== true) {
    result = replaceBrandColors(result, targetBrand);
  }

  if (options?.skipSchemeTransform !== true) {
    result = replaceSchemeColors(result, originalSvg, targetScheme);
  }

  return result;
};
